// Changing a league's rules once the league exists (LEAGUE_SETTINGS.md,
// scenario B3's write half, PLAN-2026-09-26-every-screen-and-crayon P2).
//
// ONE COPY OF THE RULE, read by the server that refuses a save and by every
// client that draws the editor: which parts a league made can still change,
// what a changed version says in sentences, and what a save needs. A second
// client with no rules engine reads the same answers off `LeagueRulesView`.

import { z } from 'zod';
import type { SportDefinition } from '@fsp/sports-core';
import { describeSettings, RulesSectionId } from './describe';
import type { LeagueSettings } from './schemas';
import type { Issue } from './validate';
import { diffSettings } from './version';

/**
 * A part of the rules a league cannot change at its stage, and why, in words
 * a commissioner reads beside the disabled field.
 *
 * THESE ARE WHAT A SEASON IS BUILT ON, and each holds from the moment
 * something is built on it (DIRECTION A17). Before the draft almost nothing
 * is: the draft row follows the rules, the calendar is built at the draft's
 * start, the budgets are set when waivers open, and the matchups are written
 * when the season opens. So a family moving draft night, or a league wanting
 * its own roster, changes it then; once a roster is drafted or a matchup
 * written, changing what it rests on is a rebuild of the season, not a new
 * version of a rule.
 */
export const FixedRule = z.object({
  /** Dot path into LeagueSettings; everything under it is fixed. */
  path: z.string(),
  why: z.string(),
});
export type FixedRule = z.infer<typeof FixedRule>;

/**
 * Where a league is in its year, for what its rules can still change
 * (PLAN-2026-10-18-families-and-newcomers §4.1).
 *
 * - `before-draft`: the league is in setup and its draft has not started.
 * - `before-season`: the draft is under way or done, and no matchup is written.
 * - `in-season`: the season is open.
 */
export const LeagueStage = z.enum(['before-draft', 'before-season', 'in-season']);
export type LeagueStage = z.infer<typeof LeagueStage>;

/** Fixed at every stage: what the league is, and its seats. */
const ALWAYS_FIXED: readonly FixedRule[] = [
  { path: 'schemaVersion', why: 'Set by the site.' },
  { path: 'sport', why: 'A league plays one sport.' },
  { path: 'season', why: 'A league plays one season at a time.' },
  {
    path: 'identity.teamCount',
    why: 'The number of teams is the seats; an open seat is closed from the league menu before the draft.',
  },
];

/** Fixed from the draft on: what a drafted roster and a budget being spent rest on. */
const FIXED_ONCE_DRAFTING: readonly FixedRule[] = [
  {
    path: 'identity.timeZone',
    why: 'Every rule time — the waiver run, the lineup lock — is planned in this zone from the draft on.',
  },
  { path: 'format', why: 'The league drafted for this format; it holds for the season.' },
  { path: 'categories', why: 'The league drafted for this format; it holds for the season.' },
  { path: 'roster', why: 'The league drafted to this roster, and every lineup is built on it.' },
  { path: 'lineup', why: 'Every week’s lineup and its locks are built on these rules.' },
  { path: 'draft', why: 'The draft has started; these are the rules it runs by.' },
  // B-135: the waiver system holds for the season; every team's budget was
  // set from it when waivers opened at the draft's end.
  {
    path: 'waivers.system',
    why: 'Every team plays by this waiver system from the draft on; it holds for the season.',
  },
  {
    path: 'waivers.faab',
    why: 'Every team’s budget is set from this when the draft ends; it holds for the season.',
  },
];

/** Fixed once the season opens: what its matchups were written from. */
const FIXED_ONCE_OPEN: readonly FixedRule[] = [
  {
    path: 'schedule',
    why: 'The season’s matchups were written from this when it opened.',
  },
  { path: 'playoffs', why: 'The playoff weeks were reserved from this when the season opened.' },
];

/**
 * Fixed in a season started from the last: how it goes on from it, chosen as
 * it started (PLAN-2026-10-27-the-next-season §2.1 rule 5). A dynasty carried
 * every roster in at that moment, and undoing that halfway is worse than
 * asking once.
 */
const FIXED_WHEN_CARRIED: readonly FixedRule[] = [
  {
    path: 'keepers.mode',
    why: 'This season’s format was chosen when it was started from the last one.',
  },
];

/**
 * What a league at `stage` cannot change, with the reason to show beside each.
 * `carried` is a season started from the last (`LeagueRef.lastSeasonId`).
 */
export function fixedRules(stage: LeagueStage, carried = false): FixedRule[] {
  return [
    ...ALWAYS_FIXED,
    ...(carried ? FIXED_WHEN_CARRIED : []),
    ...(stage === 'before-draft' ? [] : FIXED_ONCE_DRAFTING),
    ...(stage === 'in-season' ? FIXED_ONCE_OPEN : []),
  ];
}

/**
 * A league's stage from what is stored: its status, its draft's status, and
 * whether its season has been opened.
 */
export function leagueStage(input: {
  leagueStatus: string;
  draftStatus: string | null;
}): LeagueStage {
  if (input.leagueStatus === 'setup')
    return input.draftStatus === null || input.draftStatus === 'scheduled'
      ? 'before-draft'
      : 'before-season';
  // `drafting` is a league that has drafted and whose season has not opened.
  return input.leagueStatus === 'drafting' ? 'before-season' : 'in-season';
}

function under(path: string, root: string): boolean {
  return path === root || path.startsWith(`${root}.`) || path.startsWith(`${root}[`);
}

/** The fixed rules a candidate would change at `stage`. Empty when it changes none. */
export function fixedTouched(
  current: LeagueSettings,
  candidate: LeagueSettings,
  stage: LeagueStage,
  carried = false,
): FixedRule[] {
  const changed = diffSettings(current, candidate).map((d) => d.path);
  return fixedRules(stage, carried).filter((f) =>
    changed.some((p) => under(p, f.path) || under(f.path, p)),
  );
}

/** The sections a candidate changes, by their top-level key: `roster`, `draft`. */
export function sectionsTouched(current: LeagueSettings, candidate: LeagueSettings): string[] {
  return [...new Set(diffSettings(current, candidate).map((d) => d.path.split(/[.[]/)[0]!))];
}

/**
 * The roster spots the create form's roster offers (B-434), from the roster
 * the league's answers give: each spot it has, the bench, and any other
 * starting spot whose positions it already starts, unless a spot before it
 * takes exactly the same positions. So a league with a team's defense is not
 * offered defensive players' spots, which DIRECTION A12 says is never both,
 * and two spots that take the same players are offered once. In the sport's
 * order. League settings' full editor offers every spot.
 */
export function rosterSlotsOffered(
  usual: { slots: readonly { slot: string; count: number }[] },
  sport: SportDefinition,
): string[] {
  const has = new Set(usual.slots.filter((s) => s.count > 0).map((s) => s.slot));
  const started = new Set<string>();
  for (const s of usual.slots) {
    if (s.count <= 0) continue;
    const def = sport.slots.find((x) => x.id === s.slot);
    if (def?.kind === 'starter') for (const p of def.eligible) started.add(p);
  }
  const taken = new Set<string>();
  const out: string[] = [];
  for (const def of [...sport.slots].sort((a, b) => a.sortOrder - b.sortOrder)) {
    if (def.kind === 'reserve') continue;
    const takes = [...def.eligible].sort().join('/');
    const offered =
      has.has(def.id) ||
      def.kind === 'bench' ||
      (def.eligible.length > 0 &&
        def.eligible.every((p) => started.has(p)) &&
        !(def.kind === 'starter' && taken.has(takes)));
    if (!offered) continue;
    if (def.kind === 'starter') taken.add(takes);
    out.push(def.id);
  }
  return out;
}

/**
 * A draft has a round per roster spot (fewer is refused), so when a roster
 * changes and the rounds were the old roster's size, they become the new
 * one's. Rounds set to something else are left for the validator to judge.
 * Used by every editor of a roster: the create form and the league's settings.
 */
export function followRoster(before: LeagueSettings, after: LeagueSettings): LeagueSettings {
  const size = (s: LeagueSettings) => s.roster.slots.reduce((n, x) => n + x.count, 0);
  const was = size(before);
  const now = size(after);
  // A DRAFT HAS AT MOST 40 ROUNDS (`DraftSettings`), so a roster past 40 left
  // them at 40, and they still follow it back (the review of chunk 2).
  const cap = (n: number) => Math.max(1, Math.min(40, n));
  if (was === now || after.draft.rounds !== cap(was)) return after;
  return { ...after, draft: { ...after.draft, rounds: cap(now) } };
}

/** The sections of `governance.lockedAfterDraft` a candidate would change. */
export function lockedTouched(current: LeagueSettings, candidate: LeagueSettings): string[] {
  const changed = diffSettings(current, candidate).map((d) => d.path);
  return current.governance.lockedAfterDraft.filter((section) =>
    changed.some((p) => under(p, section)),
  );
}

/**
 * Everything wrong with a save beyond the rules themselves: a reason once the
 * league has drafted, a locked section changed without unlocking it, a fixed
 * rule changed at all, and the two governance choices nothing plays yet.
 * `validateLeagueSettings` is the other half; a save needs both clean.
 */
export function changeIssues(input: {
  current: LeagueSettings;
  candidate: LeagueSettings;
  stage: LeagueStage;
  drafted: boolean;
  reason: string;
  unlock: boolean;
  /** A season started from the last (`fixedRules`). */
  carried?: boolean;
}): Issue[] {
  const { current, candidate, stage, drafted, reason, unlock } = input;
  const issues: Issue[] = [];
  for (const f of fixedTouched(current, candidate, stage, input.carried ?? false)) {
    issues.push({ path: f.path, severity: 'error', message: f.why });
  }
  // LAST SEASON'S FINISH needs a last season (B-531): chosen in a league's
  // first season it would read as an order the draft cannot play. A league
  // that already had it stored is not refused for keeping it.
  if (
    !(input.carried ?? false) &&
    candidate.draft.seatOrder === 'reverse-standings' &&
    current.draft.seatOrder !== 'reverse-standings'
  ) {
    issues.push({
      path: 'draft.seatOrder',
      severity: 'error',
      message:
        'The reverse of last season’s finish needs a last season. It is offered once a season is started from one.',
    });
  }
  if (drafted) {
    if (!reason.trim()) {
      issues.push({
        path: 'reason',
        severity: 'error',
        message: 'A reason is required once the league has drafted. Everyone will see it.',
      });
    }
    const locked = lockedTouched(current, candidate);
    if (locked.length > 0 && !unlock) {
      issues.push({
        path: 'governance.lockedAfterDraft',
        severity: 'error',
        message: `${locked.join(', ')} ${locked.length === 1 ? 'is' : 'are'} locked after the draft. Unlock to change ${locked.length === 1 ? 'it' : 'them'}.`,
      });
    }
  }
  // TODO(B3): re-scoring weeks already played under a new scoring version.
  if (candidate.governance.scoringChangesApply !== 'future-periods') {
    issues.push({
      path: 'governance.scoringChangesApply',
      severity: 'error',
      message:
        'Re-scoring weeks already played is not built yet; a scoring change applies from the next week.',
    });
  }
  return issues;
}

/**
 * What one version changed, as the rules page says it: for each section that
 * moved, the sentences it no longer says and the ones it says now. Built from
 * the describer on both sides, so a change reads exactly as the rule does
 * ("0.5 points per reception") rather than as a path and two raw values.
 */
export const RulesChange = z.object({
  section: RulesSectionId,
  title: z.string(),
  was: z.array(z.string()),
  now: z.array(z.string()),
});
export type RulesChange = z.infer<typeof RulesChange>;

export function describeChanges(
  from: LeagueSettings,
  to: LeagueSettings,
  sport: SportDefinition,
): RulesChange[] {
  const before = new Map(describeSettings(from, sport).sections.map((s) => [s.id, s]));
  const out: RulesChange[] = [];
  for (const section of describeSettings(to, sport).sections) {
    const old = before.get(section.id)?.sentences ?? [];
    // AS MANY OF EACH AS THERE ARE: two identical bonuses are two sentences,
    // and removing one of them is a change.
    const was = without(old, section.sentences);
    const now = without(section.sentences, old);
    // The description is not a rule and the describer does not say it; a
    // version that changed only it still says what changed.
    if (section.id === 'league' && from.identity.description !== to.identity.description) {
      if (from.identity.description) was.push(`Description: “${from.identity.description}”`);
      now.push(
        to.identity.description ? `Description: “${to.identity.description}”` : 'No description.',
      );
    }
    if (was.length > 0 || now.length > 0) {
      out.push({ section: section.id, title: section.title, was, now });
    }
  }
  return out;
}

/** `a` minus `b`, counting repeats: each sentence of `b` cancels one of `a`. */
function without(a: readonly string[], b: readonly string[]): string[] {
  const left = new Map<string, number>();
  for (const line of b) left.set(line, (left.get(line) ?? 0) + 1);
  const out: string[] = [];
  for (const line of a) {
    const n = left.get(line) ?? 0;
    if (n > 0) left.set(line, n - 1);
    else out.push(line);
  }
  return out;
}

/**
 * What a part of the rules is called in a sentence, from a settings path:
 * "scoring", "waivers", "the league's name". The chat line a save posts
 * names the areas it changed in these words, never as keys.
 */
const AREA_WORDS: Record<string, string> = {
  identity: 'the league',
  format: 'the format',
  scoring: 'scoring',
  categories: 'the format',
  roster: 'the roster',
  lineup: 'lineups',
  schedule: 'the schedule',
  playoffs: 'the playoffs',
  tiebreakers: 'tiebreakers',
  waivers: 'waivers',
  acquisitions: 'waivers',
  trades: 'trades',
  draft: 'the draft',
  keepers: 'keepers',
  governance: 'rule changes',
  fun: 'awards',
  tools: 'helper tools',
};

export function settingsAreaName(path: string): string {
  // The weekly hat is fun but no award (PLAN-2026-10-30-the-weekly-hat).
  if (path === 'fun.hat' || path.startsWith('fun.hat.')) return 'the weekly hat';
  const top = path.split(/[.[]/)[0] ?? path;
  return AREA_WORDS[top] ?? top;
}
